import React, { useState, useEffect, useMemo, useContext, useRef, useCallback } from "react";
import StampCard from "../components/StampCard";
import { FontScaleContext } from "../App";
import { toast } from "react-toastify";

const CATEGORIES = [
    { sortOrder: 1, name: "Achievements",    description: "Show off a special day, a special milestone, or just something silly!" },
    { sortOrder: 2, name: "Competitions",    description: "Buy entries to competitions and giveaways!" },
    { sortOrder: 3, name: "Habbo Skills",    description: "Skilling items linked to your in-game Habbo!" },
    { sortOrder: 4, name: "PFP Backgrounds", description: "Background images instead of colours for your Habbo's PFP!" },
    { sortOrder: 5, name: "Profile Skins",   description: "Skins to personalise the look of your profile page." },
    { sortOrder: 6, name: "Bundles",         description: "Bundles containing multiple items at a great value!" },
];

const BURST_CLIP = 'polygon(50% 0%, 61% 12%, 77% 8%, 80% 24%, 96% 29%, 90% 44%, 100% 57%, 86% 67%, 88% 83%, 72% 84%, 64% 98%, 50% 90%, 36% 98%, 28% 84%, 12% 83%, 14% 67%, 1% 57%, 10% 44%, 5% 29%, 20% 24%, 23% 8%, 39% 12%)';

const PLACEHOLDER_IMAGE = '/img/Frank_05.png';

const MAX_FEATURED = 3;

const isFeatured = (item) => String(item.sortOrder).startsWith('999');
const getEffectiveCategoryOrder = (item) =>
    isFeatured(item) ? parseInt(String(item.sortOrder).slice(3), 10) : item.sortOrder;

function StampShack({ userData }) {
    const fontScale = useContext(FontScaleContext);
    const [shopItems, setShopItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [completedStampCards, setCompletedStampCards] = useState(0);
    const [stamps, setStamps] = useState({ totalStamps: 0, completedCards: 0 });
    const [loadingStamps, setLoadingStamps] = useState(true);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [purchasing, setPurchasing] = useState(false);
    const [activeTab, setActiveTab] = useState('featured-cont');
    const [detailItem, setDetailItem] = useState(null);
    const [showEarnModal, setShowEarnModal] = useState(false);
    const tabStripRef = useRef(null);
    const [tabOverflow, setTabOverflow] = useState({ left: false, right: false });

    useEffect(() => {
        const fetchShopData = async () => {
            try {
                setLoading(true);

                const apiUrl = process.env.REACT_APP_API_URL;

                const [itemsResponse, stampsResponse] = await Promise.all([
                    fetch(`${apiUrl}shop/items`, {
                        credentials: 'include'
                    }),
                    userData?.userId ? fetch(`${apiUrl}stamps/my-stamps`, {
                        credentials: 'include'
                    }) : Promise.resolve({ json: () => ({ success: false }) })
                ]);

                const itemsData = await itemsResponse.json();
                const stampsData = await stampsResponse.json();

                if (itemsData.success) {
                    setShopItems(itemsData.items);
                    const featured = itemsData.items.filter(isFeatured);
                    setActiveTab(featured.length > MAX_FEATURED ? 'featured-cont' : 'all');
                }

                if (stampsData.success) {
                    setCompletedStampCards(stampsData.completedCards || 0);
                    setStamps({
                        totalStamps: stampsData.totalStamps || 0,
                        completedCards: stampsData.completedCards || 0,
                    });
                    setLoadingStamps(false);
                }
            } catch (error) {
                console.error('Error fetching shop data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchShopData();
    }, [userData?.userId]);

    const stickyPalette = ["#FFF3A3", "#FFE29A", "#CFF3B2", "#BFE7FF", "#F8C6D8", "#D8C7FF"];

    const handlePurchaseClick = (item) => {
        setDetailItem(null);
        setSelectedItem(item);
        setShowConfirmModal(true);
    };

    const handleConfirmPurchase = async () => {
        if (!selectedItem) return;

        try {
            setPurchasing(true);

            const apiUrl = process.env.REACT_APP_API_URL;
            const response = await fetch(`${apiUrl}shop/purchase`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({
                    itemId: selectedItem.id
                })
            });

            const data = await response.json();

            if (data.success) {
                toast.success('Purchase successful! Check your messages for details.');

                setShowConfirmModal(false);
                setSelectedItem(null);

                const [itemsResponse, stampsResponse] = await Promise.all([
                    fetch(`${apiUrl}shop/items`, { credentials: 'include' }),
                    fetch(`${apiUrl}stamps/my-stamps`, { credentials: 'include' })
                ]);

                const itemsData = await itemsResponse.json();
                const stampsData = await stampsResponse.json();

                if (itemsData.success) setShopItems(itemsData.items);
                if (stampsData.success) {
                    setCompletedStampCards(stampsData.completedCards || 0);
                    setStamps({
                        totalStamps: stampsData.totalStamps || 0,
                        completedCards: stampsData.completedCards || 0,
                    });
                }
            } else {
                toast.error(data.message || 'Failed to complete purchase');
            }
        } catch (error) {
            console.error('Error purchasing item:', error);
            toast.error('Failed to complete purchase. Please try again.');
        } finally {
            setPurchasing(false);
        }
    };

    const handleCancelPurchase = () => {
        setShowConfirmModal(false);
        setSelectedItem(null);
    };

    const stickyColorsById = useMemo(() => {
        const map = {};
        for (let i = 0; i < shopItems.length; i++) {
            const item = shopItems[i];
            let hash = 0;
            for (let j = 0; j < item.id.length; j++) hash = (hash * 31 + item.id.charCodeAt(j)) >>> 0;
            map[item.id] = stickyPalette[hash % stickyPalette.length];
        }
        return map;
    }, [shopItems]);

    const featuredItems = useMemo(() => shopItems.filter(isFeatured), [shopItems]);
    const featuredPostIts = useMemo(() => featuredItems.slice(0, MAX_FEATURED), [featuredItems]);
    const featuredOverflow = useMemo(() => featuredItems.slice(MAX_FEATURED), [featuredItems]);

    const availableTabs = useMemo(() => {
        const tabs = [];
        if (featuredItems.length > 0) tabs.push({ key: 'featured-cont', label: 'Featured', isYellow: true, desktopHidden: featuredItems.length <= MAX_FEATURED });
        tabs.push({ key: 'all', label: 'All' });
        for (const cat of CATEGORIES) {
            if (shopItems.some(i => getEffectiveCategoryOrder(i) === cat.sortOrder)) {
                tabs.push({ key: cat.sortOrder, label: cat.name });
            }
        }
        return tabs;
    }, [shopItems, featuredItems]);

    const updateTabOverflow = useCallback(() => {
        const el = tabStripRef.current;
        if (!el) return;
        const maxScroll = el.scrollWidth - el.clientWidth;
        setTabOverflow({
            left: el.scrollLeft > 1,
            right: el.scrollLeft < maxScroll - 1,
        });
    }, []);

    useEffect(() => {
        const el = tabStripRef.current;
        if (!el) return;
        updateTabOverflow();
        const observer = new ResizeObserver(updateTabOverflow);
        observer.observe(el);
        Array.from(el.children).forEach(child => observer.observe(child));
        window.addEventListener('resize', updateTabOverflow);
        return () => {
            observer.disconnect();
            window.removeEventListener('resize', updateTabOverflow);
        };
    }, [updateTabOverflow, availableTabs, loading]);

    const scrollTabs = (direction) => {
        const el = tabStripRef.current;
        if (!el) return;
        el.scrollBy({ left: direction * Math.max(120, el.clientWidth * 0.6), behavior: 'smooth' });
    };

    const listGroups = useMemo(() => {
        if (activeTab === 'featured-cont') {
            return [{ label: 'Featured', items: featuredItems, isFeaturedCont: true }];
        }
        if (activeTab !== 'all') {
            const cat = CATEGORIES.find(c => c.sortOrder === activeTab);
            const items = shopItems.filter(i => getEffectiveCategoryOrder(i) === activeTab);
            return items.length > 0 ? [{ label: cat?.name || '', description: cat?.description || '', items }] : [];
        }
        const groups = [];
        for (const cat of CATEGORIES) {
            const items = shopItems.filter(i => getEffectiveCategoryOrder(i) === cat.sortOrder);
            if (items.length > 0) groups.push({ label: cat.name, description: cat.description, items });
        }
        return groups;
    }, [activeTab, shopItems, featuredItems]);

    const hasListItems = shopItems.length > 0;

    return (
        <div className="w-full min-h-screen relative overflow-hidden">
            <div
                className="w-full h-[63px] z-50 relative"
                style={{
                    backgroundImage: "url('/img/shoptop.png')",
                    backgroundRepeat: "round",
                    backgroundPosition: "top center",
                    backgroundSize: "auto 63px",
                    imageRendering: "pixelated",
                }}
            >
            </div>

            <div className="w-full p-4 lg2:p-6">
                {/* Logo — mobile only, sits above the grid */}
                <div className="flex justify-center lg2:hidden mb-4">
                    <div className="relative inline-block">
                        <img
                            src="/img/franks-stamp-shack_4x.png"
                            alt="Frank's Framp Shack!"
                            className="block w-auto h-auto max-w-none"
                            style={{ imageRendering: "pixelated" }}
                        />
                        <img
                            src="/img/stampcard_stamp.png"
                            className="absolute top-[480px] left-[140px] z-10"
                            alt=""
                            style={{
                                imageRendering: "pixelated",
                                filter: "brightness(0) saturate(100%) invert(20%) sepia(60%) saturate(600%) hue-rotate(10deg) brightness(0.3)",
                            }}
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-4 lg2:gap-6 lg2:grid-cols-[auto_minmax(0,1fr)] items-start">
                    {/* Left column — stamp card (on mobile just the stamp card, logo is above the grid) */}
                    <div className="flex justify-center flex-col lg2:justify-start">
                        <div className="relative hidden lg2:inline-block">
                            <img
                                src="/img/franks-stamp-shack_4x.png"
                                alt="Frank's Framp Shack!"
                                className="block w-auto h-auto max-w-none"
                                style={{ imageRendering: "pixelated" }}
                            />
                            <img
                                src="/img/stampcard_stamp.png"
                                className="absolute top-[480px] left-[140px] z-10"
                                alt=""
                                style={{
                                    imageRendering: "pixelated",
                                    filter: "brightness(0) saturate(100%) invert(20%) sepia(60%) saturate(600%) hue-rotate(10deg) brightness(0.3)",
                                }}
                            />
                        </div>

                        <div name="StampCardContainer" className="relative z-30 flex items-center lg2:mt-5 justify-center w-full">
                            <div style={{ zoom: 1 / fontScale }}>
                                <StampCard
                                    userData={userData}
                                    stamps={stamps.totalStamps}
                                    completedCards={stamps.completedCards}
                                    loading={loadingStamps}
                                />
                            </div>
                        </div>

                        <div className="relative z-30 flex justify-center mt-3">
                            <button
                                type="button"
                                onClick={() => setShowEarnModal(true)}
                                className="font-['Jersey_10'] tracking-widest uppercase text-[15px] text-white/80 hover:text-white transition-colors underline underline-offset-2 decoration-white/40 hover:decoration-white"
                            >
                                How do I earn framps?
                            </button>
                        </div>
                    </div>

                    {/* Right column — first on mobile via order-first, normal on desktop */}
                    <div className="relative order-first lg2:order-none">
                        <img
                            src="/img/stampcard_stamp.png"
                            className="absolute top-[-30px] left-[-10px] z-10"
                            alt=""
                            style={{
                                imageRendering: "pixelated",
                                scale: 2,
                                transform: "rotate(30deg)",
                                opacity: 0.8,
                            }}
                        />
                        <img
                            src="/img/stampcard_stamp.png"
                            className="absolute top-[40%] right-[-10px] z-10"
                            alt=""
                            style={{
                                imageRendering: "pixelated",
                                scale: 2,
                                transform: "rotate(-40deg)",
                                opacity: 0.8,
                            }}
                        />
                        <img
                            src="/img/stampcard_stamp.png"
                            className="absolute bottom-[-15px] left-[30%] z-10"
                            alt=""
                            style={{
                                imageRendering: "pixelated",
                                scale: 2,
                                transform: "rotate(-10deg)",
                                opacity: 0.8,
                            }}
                        />

                        <div className="w-full bg-coal_dark p-2 text-right text-md text-white">
                            <div className="relative z-10">You currently have <b>{completedStampCards}</b> completed framp cards!</div>
                        </div>

                        <div
                            className="relative min-w-0 w-full min-h-[600px] border border-black p-4 lg2:p-6"
                            style={{
                                backgroundImage: "url('/img/shack-bg.png')",
                                backgroundRepeat: "repeat",
                                backgroundPosition: "top left",
                            }}
                        >
                            {loading ? (
                                <div className="text-center py-12">
                                    <p className="text-gray-700 font-['Jersey_10'] tracking-widest uppercase text-[18px]">Loading shop...</p>
                                </div>
                            ) : shopItems.length === 0 ? (
                                <div className="text-center py-12">
                                    <p className="text-gray-700 font-['Jersey_10'] tracking-widest uppercase text-[18px]">No items available</p>
                                </div>
                            ) : (
                                <div className="relative z-20">
                                    {/* Featured post-it notes (max 3) */}
                                    {featuredPostIts.length > 0 && (
                                        <>
                                        <div
                                            className="hidden md:block w-full text-center font-['Jersey_10'] tracking-widest uppercase text-[18px] leading-none px-4 py-2 mb-2 border border-black/40"
                                            style={{
                                                background: 'linear-gradient(to bottom, rgba(255,255,255,0.6), rgba(255,255,240,0.3))',
                                                boxShadow: '0 2px 0 rgba(0,0,0,0.2)',
                                                color: '#5A3800',
                                                textShadow: '0 1px 0 rgba(255,255,255,0.6)',
                                            }}
                                        >
                                            ★ Frank's Featured Items! ★
                                        </div>
                                        <div
                                            className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 mx-auto w-full max-w-[1100px] gap-6 p-3 overflow-hidden mb-6"
                                        >
                                            {featuredPostIts.map((item, index) => {
                                                const isLoggedIn = !!userData;
                                                const canRedeem = isLoggedIn && completedStampCards >= item.currentPrice && item.canPurchase;
                                                const stickyColor = stickyColorsById[item.id];
                                                const hasDiscount = item.discount !== null;

                                                return (
                                                    <div key={item.id} className={index >= 2 ? 'hidden lg:block' : ''}>
                                                    <div
                                                        className="w-full border border-black flex flex-col relative"
                                                        style={{
                                                            backgroundColor: stickyColor,
                                                            boxShadow: "0 2px 0 rgba(0,0,0,0.35), 0 14px 22px rgba(0,0,0,0.28)",
                                                            transform: `rotate(${((item.id.length * 7) % 7) - 3}deg)`,
                                                            transformOrigin: "center",
                                                        }}
                                                    >
                                                        <div
                                                            className="absolute top-0 left-0 right-0 h-[22px] border-b border-black"
                                                            style={{
                                                                background: "linear-gradient(to bottom, rgba(255,255,255,0.55), rgba(255,255,255,0))",
                                                            }}
                                                        />

                                                        <div
                                                            className="absolute top-[-10px] left-1/2 -translate-x-1/2 w-[88px] h-[28px] border border-black"
                                                            style={{
                                                                background: "repeating-linear-gradient(45deg, rgba(255,255,255,0.55) 0 6px, rgba(255,255,255,0.2) 6px 12px)",
                                                                boxShadow: "0 2px 0 rgba(0,0,0,0.25)",
                                                            }}
                                                        />

                                                        {hasDiscount && (
                                                            <div
                                                                className="absolute top-[-8px] right-[-8px] w-[60px] h-[60px] border-2 border-black rounded-full bg-red-500 flex items-center justify-center z-20"
                                                                style={{ boxShadow: "0 3px 0 rgba(0,0,0,0.4)" }}
                                                            >
                                                                <div className="text-center">
                                                                    <div className="text-white font-['Jersey_10'] tracking-widest text-[20px] leading-[18px]">
                                                                        {item.discount.type === 'percentage'
                                                                            ? `${item.discount.value}%`
                                                                            : `${Math.round(item.discount.value / item.basePrice * 100)}%`
                                                                        }
                                                                    </div>
                                                                    <div className="text-white font-['Jersey_10'] tracking-widest text-[12px] leading-[12px] uppercase">OFF!</div>
                                                                </div>
                                                            </div>
                                                        )}

                                                        <div className="px-3 pt-6">
                                                            <div className="text-black font-['Jersey_10'] tracking-widest uppercase text-[22px] leading-[22px] border-b-[1px] border-black/50 truncate">
                                                                {item.name}
                                                            </div>
                                                            <div className="font-['Jersey_10'] tracking-widest text-md">
                                                                {item.description}
                                                            </div>
                                                        </div>

                                                        <div className="px-3 pt-3">
                                                            <div className="w-full aspect-[4/3] border border-black bg-white/55 flex items-center justify-center">
                                                                {item.image ? (
                                                                    <img
                                                                        src={item.image}
                                                                        alt={item.name}
                                                                        className="max-w-full max-h-full"
                                                                        style={{ imageRendering: "pixelated" }}
                                                                    />
                                                                ) : (
                                                                    <div className="text-black font-['Jersey_10'] tracking-widest uppercase text-[18px]">No Image</div>
                                                                )}
                                                            </div>
                                                        </div>

                                                        <div className="px-3 pt-3">
                                                            <div className="grid grid-cols-2 gap-2">
                                                                <div className="border border-black bg-white/60 px-2 py-2">
                                                                    <div className="text-black font-['Jersey_10'] tracking-widest uppercase text-[16px] leading-[16px]">
                                                                        Framp Card Cost
                                                                    </div>
                                                                    {hasDiscount ? (
                                                                        <div className="flex items-baseline gap-1">
                                                                            <div className="text-black font-['Jersey_10'] tracking-widest text-[22px] leading-[22px] font-bold">
                                                                                {item.currentPrice}
                                                                            </div>
                                                                            <div className="text-black/40 font-['Jersey_10'] tracking-widest text-[18px] leading-[18px] line-through">
                                                                                {item.basePrice}
                                                                            </div>
                                                                        </div>
                                                                    ) : (
                                                                        <div className="text-black font-['Jersey_10'] tracking-widest text-[22px] leading-[22px]">
                                                                            {item.currentPrice}
                                                                        </div>
                                                                    )}
                                                                </div>

                                                                <div className="border border-black bg-white/60 px-2 py-2 relative">
                                                                    <div className="text-black font-['Jersey_10'] tracking-widest uppercase text-[16px] leading-[16px]">
                                                                        {item.globalStock && item.globalStock > 0 ? "Total Stock" : "Stock per Person"}
                                                                    </div>
                                                                    <div className="text-black font-['Jersey_10'] tracking-widest text-[22px] leading-[22px]">
                                                                        {item.globalStockRemaining !== null
                                                                            ? item.globalStockRemaining
                                                                            : item.remainingStock}
                                                                    </div>
                                                                    {item.globalStockRemaining !== null && item.stockPerUser > 0 && (
                                                                        <div className="absolute right-2 bottom-0 text-black/70 font-['Jersey_10'] tracking-widest text-right uppercase text-[12px] leading-[14px]">
                                                                            Max. {item.stockPerUser} per person
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div className="px-3 py-3 mt-auto">
                                                            <button
                                                                type="button"
                                                                disabled={!canRedeem}
                                                                onClick={() => canRedeem && handlePurchaseClick(item)}
                                                                className={`w-full border border-black px-3 py-2 font-['Jersey_10'] tracking-widest uppercase text-[20px] leading-[20px] ${
                                                                    canRedeem
                                                                        ? "bg-white hover:bg-white/90 active:bg-white/80 text-black"
                                                                        : "bg-black/20 text-black/50 cursor-not-allowed"
                                                                }`}
                                                            >
                                                                {isLoggedIn ? (
                                                                    item.alreadyOwned ? "Already Owned!" : item.canPurchase ? "Redeem!" : "Sold Out!"
                                                                ) : (
                                                                    "Login to Redeem!"
                                                                )}
                                                            </button>
                                                        </div>
                                                    </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        </>
                                    )}

                                    {/* All items header */}
                                    {hasListItems && (
                                        <div
                                            className="w-full text-center font-['Jersey_10'] tracking-widest uppercase text-[18px] leading-none px-4 py-2 mb-2 border border-black/40"
                                            style={{
                                                background: 'linear-gradient(to bottom, rgba(255,255,255,0.6), rgba(255,255,240,0.3))',
                                                boxShadow: '0 2px 0 rgba(0,0,0,0.2)',
                                                color: '#5A3800',
                                                textShadow: '0 1px 0 rgba(255,255,255,0.6)',
                                            }}
                                        >
                                            ★ All Items ★
                                        </div>
                                    )}

                                    {/* Category list section */}
                                    {hasListItems && (
                                        <div
                                            className="rounded-lg p-5"
                                            style={{
                                                backgroundColor: '#7B4520',
                                                borderTop:    '1px solid #C47840',
                                                borderLeft:   '1px solid #C47840',
                                                borderBottom: '1px solid #2E0E00',
                                                borderRight:  '1px solid #2E0E00',
                                                boxShadow: '0 4px 10px rgba(0,0,0,0.45)',
                                            }}
                                        >
                                            <div
                                                className="overflow-hidden"
                                                style={{
                                                    borderRadius: 4,
                                                    borderTop:    '1px solid #3A1206',
                                                    borderLeft:   '1px solid #3A1206',
                                                    borderBottom: '1px solid #B87840',
                                                    borderRight:  '1px solid #B87840',
                                                }}
                                            >
                                                {/* Tabs */}
                                                <div className="relative" style={{ backgroundColor: '#5C3010', borderBottom: '1px solid #3A1206' }}>
                                                <div
                                                    ref={tabStripRef}
                                                    onScroll={updateTabOverflow}
                                                    className="flex overflow-x-auto"
                                                    style={{ scrollbarWidth: 'none' }}
                                                >
                                                    {availableTabs.map(tab => {
                                                        const isActive = activeTab === tab.key;
                                                        const isYellow = tab.isYellow;
                                                        return (
                                                            <button
                                                                key={tab.key}
                                                                type="button"
                                                                onClick={() => setActiveTab(tab.key)}
                                                                className={`px-4 py-2 font-['Jersey_10'] tracking-widest uppercase text-[16px] transition-colors whitespace-nowrap ${
                                                                    tab.desktopHidden ? 'md:hidden' : ''
                                                                } ${
                                                                    isActive
                                                                        ? 'text-black'
                                                                        : isYellow ? 'text-[#C9A227] hover:bg-[#7A4A22]' : 'text-[#E8D4B0] hover:bg-[#7A4A22]'
                                                                }`}
                                                                style={isActive
                                                                    ? { backgroundColor: isYellow ? '#C9A227' : '#F2E4CC', borderRight: '1px solid #3A1206' }
                                                                    : { backgroundColor: '#5C3010', borderRight: '1px solid #2A0A00' }
                                                                }
                                                            >
                                                                {tab.label}
                                                            </button>
                                                        );
                                                    })}
                                                </div>

                                                {tabOverflow.left && (
                                                    <button
                                                        type="button"
                                                        aria-label="Scroll categories left"
                                                        onClick={() => scrollTabs(-1)}
                                                        className="absolute inset-y-0 left-0 px-2 font-['Jersey_10'] text-[18px] leading-none text-[#E8D4B0] hover:text-white hover:bg-[#7A4A22] active:bg-[#8A5A2A]"
                                                        style={{
                                                            backgroundColor: '#5C3010',
                                                            borderRight: '1px solid #2A0A00',
                                                            boxShadow: '6px 0 8px -2px rgba(0,0,0,0.55)',
                                                        }}
                                                    >
                                                        ◄
                                                    </button>
                                                )}

                                                {tabOverflow.right && (
                                                    <button
                                                        type="button"
                                                        aria-label="Scroll categories right"
                                                        onClick={() => scrollTabs(1)}
                                                        className="absolute inset-y-0 right-0 px-2 font-['Jersey_10'] text-[18px] leading-none text-[#E8D4B0] hover:text-white hover:bg-[#7A4A22] active:bg-[#8A5A2A]"
                                                        style={{
                                                            backgroundColor: '#5C3010',
                                                            borderLeft: '1px solid #2A0A00',
                                                            boxShadow: '-6px 0 8px -2px rgba(0,0,0,0.55)',
                                                        }}
                                                    >
                                                        ►
                                                    </button>
                                                )}
                                                </div>

                                                {/* List content */}
                                                <style>{`.habbo-scroll::-webkit-scrollbar{width:16px}.habbo-scroll::-webkit-scrollbar-track{background:#cfcfcf;border:1px solid #000}.habbo-scroll::-webkit-scrollbar-thumb{background:#8f8f8f;border:1px solid #000}.habbo-scroll::-webkit-scrollbar-thumb:hover{background:#7a7a7a}`}</style>
                                                <div className="habbo-scroll" style={{ backgroundColor: '#F2E4CC', height: 400, overflowY: 'auto' }}>
                                                    {listGroups.length === 0 ? (
                                                        <div className="text-center py-8 font-['Jersey_10'] tracking-widest uppercase text-[16px] text-black/50">
                                                            No items in this category
                                                        </div>
                                                    ) : (
                                                        listGroups.map(group => (
                                                            <div key={group.label}>
                                                                {/* Category header */}
                                                                <div
                                                                    className={`px-4 py-2 flex items-baseline gap-3 border-b border-black ${
                                                                        group.isFeaturedCont
                                                                            ? 'bg-yellow-200 text-yellow-900'
                                                                            : 'text-black'
                                                                    }`}
                                                                    style={!group.isFeaturedCont ? { backgroundColor: '#D4B896' } : {}}
                                                                >
                                                                    <span className="font-['Jersey_10'] tracking-widest uppercase text-[16px] leading-none flex-shrink-0">{group.label}</span>
                                                                    {group.description && (
                                                                        <span className="font-['Jersey_10'] tracking-widest text-[12px] normal-case opacity-60 truncate">{group.description}</span>
                                                                    )}
                                                                </div>

                                                                {/* List rows */}
                                                                {group.items.map((item, idx) => {
                                                                    const hasDiscount = item.discount !== null;
                                                                    const hasGlobalStock = item.globalStockRemaining !== null;

                                                                    const discountLabel = hasDiscount
                                                                        ? (item.discount.type === 'percentage'
                                                                            ? `${Math.round(item.discount.value)}%`
                                                                            : `${Math.round(item.discount.value / item.basePrice * 100)}%`)
                                                                        : null;

                                                                    const burstClip = BURST_CLIP;

                                                                    const Thumbnail = ({ size }) => (
                                                                        <div className="border border-black overflow-hidden relative bg-white/60" style={{ width: size, height: size }}>
                                                                            {item.image ? (
                                                                                <img src={item.image} alt="" style={{ imageRendering: 'pixelated', position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }} />
                                                                            ) : (
                                                                                <div className="absolute inset-0 flex items-center justify-center font-['Jersey_10'] tracking-widest text-xs uppercase text-black/40">No Image</div>
                                                                            )}
                                                                        </div>
                                                                    );

                                                                    return (
                                                                        <div key={item.id}>
                                                                            {/* ── Mobile layout ── */}
                                                                            <div className={`md:hidden flex items-center gap-2 px-3 py-2 border-b border-black/20 ${idx % 2 === 0 ? 'bg-white/20' : 'bg-white/40'}`}>
                                                                                <div className="relative flex-shrink-0" style={{ width: 48, minWidth: 48 }}>
                                                                                    <Thumbnail size={48} />
                                                                                </div>
                                                                                <div className="flex-1 min-w-0 relative">

                                                                                    {hasDiscount && (
                                                                                        <div className="absolute z-10 -top-1 -right-1" style={{ width: 42, height: 42 }}>
                                                                                            <div className="absolute inset-0" style={{ backgroundColor: '#DC2626', clipPath: burstClip }} />
                                                                                            <div className="absolute inset-0 flex flex-col items-center justify-between z-10" style={{ padding: '5px 2px' }}>
                                                                                                <span className="font-['Jersey_10'] tracking-widest text-white leading-none text-center text-md absolute top-[12px]">{discountLabel}</span>
                                                                                                <span className="font-['Jersey_10'] tracking-normal text-white leading-none uppercase absolute bottom-1.5" style={{ fontSize: 9 }}>off!</span>
                                                                                            </div>
                                                                                        </div>
                                                                                    )}

                                                                                    <div className="font-['Jersey_10'] tracking-widest uppercase text-[16px] leading-[16px] truncate">
                                                                                        {isFeatured(item) && <span className="text-gold mr-1">★</span>}
                                                                                        {item.name}
                                                                                    </div>
                                                                                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                                                                                        <span className="font-['Jersey_10'] tracking-widest text-sm leading-none text-black/70">
                                                                                            {hasDiscount
                                                                                                ? item.currentPrice
                                                                                                : item.currentPrice
                                                                                            }
                                                                                            {' '}framps
                                                                                        </span>
                                                                                        <span className="text-black/40 text-xs">·</span>
                                                                                        <span className="font-['Jersey_10'] tracking-widest text-sm leading-none text-black/70">
                                                                                            {hasGlobalStock ? `${item.globalStockRemaining} left` : `${item.remainingStock}/person`}
                                                                                        </span>
                                                                                    </div>
                                                                                </div>
                                                                                <button type="button" onClick={() => setDetailItem(item)} className="flex-shrink-0 border border-black bg-white/60 hover:bg-white active:bg-white/80 px-2 py-1 font-['Jersey_10'] tracking-widest uppercase text-sm whitespace-nowrap transition-colors">
                                                                                    Redeem
                                                                                </button>
                                                                            </div>

                                                                            {/* ── Desktop layout ── */}
                                                                            <div className={`hidden md:flex items-center gap-3 px-4 py-2 border-b border-black/20 ${idx % 2 === 0 ? 'bg-white/20' : 'bg-white/40'}`}>
                                                                                <div className="flex-shrink-0" style={{ width: 64, minWidth: 64 }}>
                                                                                    <Thumbnail size={64} />
                                                                                </div>
                                                                                <div className="flex-1 min-w-0">
                                                                                    <div className="font-['Jersey_10'] tracking-widest uppercase text-[18px] leading-[18px] truncate">
                                                                                        {isFeatured(item) && <span className="text-gold mr-1">★</span>}
                                                                                        {item.name}
                                                                                    </div>
                                                                                    <div className="text-xs text-black/60 truncate mt-0.5">{item.description}</div>
                                                                                </div>

                                                                                <div className="relative flex-shrink-0 text-right">
                                                                                    {hasDiscount && (
                                                                                        <div className="absolute z-10 right-full mr-[14px] top-1/2 -translate-y-1/2 w-[72px] h-[72px]">
                                                                                            <div className="absolute inset-0 bg-red-600" style={{ clipPath: burstClip }} />
                                                                                            <div className="absolute inset-0 flex flex-col items-center justify-between z-10 px-[3px] py-[7px]">
                                                                                                <span className="font-['Jersey_10'] tracking-normal text-white leading-none uppercase whitespace-nowrap absolute top-4 text-[10px]">Reduced!</span>
                                                                                                <span className="font-['Jersey_10'] tracking-widest text-white text-lg leading-none text-center absolute top-7">{discountLabel}</span>
                                                                                                <span className="font-['Jersey_10'] tracking-normal text-white leading-none uppercase absolute bottom-3.5 text-[10px]">off!</span>
                                                                                            </div>
                                                                                        </div>
                                                                                    )}
                                                                                    <div className="flex items-center justify-end gap-1">
                                                                                        <div className="font-['Jersey_10'] tracking-widest uppercase text-[14px] leading-[14px] text-black/60">Cost</div>
                                                                                    </div>
                                                                                    <div className="font-['Jersey_10'] tracking-widest text-[20px]">{item.currentPrice}</div>
                                                                                    {hasDiscount &&
                                                                                        <div className=" line-through absolute top-full right-0 font-['Jersey_10'] tracking-widest text-xs leading-none text-black/50 whitespace-nowrap mt-0.5">
                                                                                            {item.basePrice}
                                                                                        </div>}
                                                                                </div>

                                                                                <div className="relative flex-shrink-0 text-right">
                                                                                    {hasGlobalStock ? (
                                                                                        <>
                                                                                            <div className="font-['Jersey_10'] tracking-widest uppercase text-[14px] leading-[14px] text-black/60">Total Stock</div>
                                                                                            <div className="font-['Jersey_10'] tracking-widest text-[20px]">{item.globalStockRemaining}</div>
                                                                                            {item.stockPerUser > 0 && (
                                                                                                <div className="absolute top-full right-0 font-['Jersey_10'] tracking-widest text-xs leading-none text-black/50 whitespace-nowrap mt-0.5">
                                                                                                    Max {item.stockPerUser}/person
                                                                                                </div>
                                                                                            )}
                                                                                        </>
                                                                                    ) : (
                                                                                        <>
                                                                                            <div className="font-['Jersey_10'] tracking-widest uppercase text-[14px] leading-[14px] text-black/60">Per Person</div>
                                                                                            <div className="font-['Jersey_10'] tracking-widest text-[20px]">{item.remainingStock}</div>
                                                                                        </>
                                                                                    )}
                                                                                </div>

                                                                                <button type="button" onClick={() => setDetailItem(item)} className="flex-shrink-0 border border-black bg-white/60 hover:bg-white active:bg-white/80 px-3 py-1 font-['Jersey_10'] tracking-widest uppercase text-[15px] leading-[15px] whitespace-nowrap transition-colors">
                                                                                    Redeem...
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        ))
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Item detail modal */}
            {detailItem && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center">
                    <div
                        className="absolute inset-0 bg-black bg-opacity-50"
                        onClick={() => setDetailItem(null)}
                    />

                    <div
                        className="relative bg-offwhite border-2 border-gray-800 p-6 max-w-lg w-full mx-4 z-[201] overflow-y-auto max-h-[90vh]"
                        style={{ boxShadow: '0 4px 0 0 rgba(0, 0, 0, 0.5)' }}
                    >
                        <h2 className="text-maroon uppercase font-['Jersey_10'] tracking-widest text-[20px] tracking-widest mb-2">
                            {detailItem.name}
                        </h2>

                        <hr className="border-gold mb-4" />

                        {/* Full image */}
                        <div className="w-full border border-black bg-white flex items-center justify-center mb-4 p-4 min-h-[120px]">
                            {detailItem.image ? (
                                <img
                                    src={detailItem.image}
                                    alt={detailItem.name}
                                    className="max-w-full"
                                    style={{ imageRendering: 'pixelated' }}
                                />
                            ) : (
                                <div className="py-8 text-black/40 font-['Jersey_10'] tracking-widest uppercase text-[16px]">No Image</div>
                            )}
                        </div>

                        {/* Description */}
                        {detailItem.description && (
                            <p className="text-gray-700 text-sm mb-4">{detailItem.description}</p>
                        )}

                        {/* Cost + Stock */}
                        {(() => {
                            const modalDiscountLabel = detailItem.discount !== null
                                ? (detailItem.discount.type === 'percentage'
                                    ? `${Math.round(detailItem.discount.value)}%`
                                    : `-${Math.round(detailItem.discount.value)}`)
                                : null;
                            return (
                        <div className="grid grid-cols-2 gap-3 mb-4">
                            <div className="border border-black bg-gray-50 px-3 py-2 relative overflow-visible">
                                {detailItem.discount !== null && (
                                    <div className="absolute z-10 -right-2 -top-2 w-[72px] h-[72px]">
                                        <div className="absolute inset-0 bg-red-600" style={{ clipPath: BURST_CLIP }} />
                                        <div className="absolute inset-0 flex flex-col items-center justify-between z-10 px-[3px] py-[7px]">
                                            <span className="font-['Jersey_10'] tracking-normal text-white leading-none uppercase whitespace-nowrap absolute top-4 text-[10px]">Reduced!</span>
                                            <span className="font-['Jersey_10'] tracking-widest text-white text-lg leading-none text-center absolute top-7">{modalDiscountLabel}</span>
                                            <span className="font-['Jersey_10'] tracking-normal text-white leading-none uppercase absolute bottom-3.5 text-[10px]">off!</span>
                                        </div>
                                    </div>
                                )}

                                <div className="flex items-center gap-1 relative">
                                    <div className="font-['Jersey_10'] tracking-widest uppercase text-[14px] text-gray-600 leading-[14px]">
                                        Framp Card Cost
                                    </div>
                                </div>
                                <div className="font-['Jersey_10'] tracking-widest text-[24px] leading-[24px]">{detailItem.currentPrice}</div>
                                {detailItem.discount !== null && (
                                    <div className="font-['Jersey_10'] tracking-widest text-[14px] leading-[14px] text-black/40 line-through">{detailItem.basePrice}</div>
                                )}
                            </div>

                            <div className="border border-black bg-gray-50 px-3 py-2">
                                <div className="font-['Jersey_10'] tracking-widest uppercase text-[14px] text-gray-600 leading-[14px] mb-1">
                                    {detailItem.globalStock && detailItem.globalStock > 0 ? "Total Stock" : "Stock per Person"}
                                </div>
                                <div className="font-['Jersey_10'] tracking-widest text-[24px]">
                                    {detailItem.globalStockRemaining !== null
                                        ? detailItem.globalStockRemaining
                                        : detailItem.remainingStock}
                                </div>
                                {detailItem.globalStockRemaining !== null && detailItem.stockPerUser > 0 && (
                                    <div className="text-xs text-black/60 mt-0.5">
                                        Max. {detailItem.stockPerUser} per person
                                    </div>
                                )}
                            </div>
                        </div>
                            );
                        })()}

                        {/* Actions */}
                        <div className="flex flex-col-reverse sm:flex-row gap-3 justify-end">
                            <button
                                type="button"
                                onClick={() => setDetailItem(null)}
                                className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-6 py-2 font-['Jersey_10'] tracking-widest uppercase text-[17px] tracking-widest transition-colors"
                                style={{ boxShadow: '0 3px 0 0 rgba(0, 0, 0, 0.3)' }}
                            >
                                Close
                            </button>
                            {(() => {
                                const isLoggedIn = !!userData;
                                const canRedeem = isLoggedIn && completedStampCards >= detailItem.currentPrice && detailItem.canPurchase;
                                return (
                                    <button
                                        type="button"
                                        disabled={!canRedeem}
                                        onClick={() => canRedeem && handlePurchaseClick(detailItem)}
                                        className={`px-6 py-2 font-['Jersey_10'] tracking-widest uppercase text-[17px] tracking-widest transition-opacity ${
                                            canRedeem
                                                ? 'text-white hover:opacity-90'
                                                : 'bg-black/20 text-black/50 cursor-not-allowed'
                                        }`}
                                        style={canRedeem
                                            ? { boxShadow: '0 3px 0 0 rgba(0, 0, 0, 0.3)', backgroundColor: '#5A2929' }
                                            : {}
                                        }
                                    >
                                        {isLoggedIn
                                            ? (detailItem.alreadyOwned ? 'Already Owned!' : detailItem.canPurchase ? 'Redeem!' : 'Sold Out!')
                                            : 'Login to Redeem!'
                                        }
                                    </button>
                                );
                            })()}
                        </div>
                    </div>
                </div>
            )}

            {/* Confirm purchase modal */}
            {showConfirmModal && selectedItem && (
                <div className="fixed inset-0 z-[210] flex items-center justify-center">
                    <div
                        className="absolute inset-0 bg-black bg-opacity-50"
                        onClick={handleCancelPurchase}
                    />

                    <div
                        className="relative bg-offwhite border-2 border-gray-800 p-6 max-w-md w-full mx-4 z-[211]"
                        style={{ boxShadow: '0 4px 0 0 rgba(0, 0, 0, 0.5)' }}
                    >
                        <h2 className="text-maroon uppercase font-['Jersey_10'] tracking-widest text-[20px] tracking-widest mb-4">
                            Confirm Purchase
                        </h2>

                        <hr className="border-gold mb-4" />

                        <div className="mb-4">
                            <p className="text-gray-700 mb-2">
                                Are you sure you want to purchase:
                            </p>
                            <p className="font-['Jersey_10'] tracking-widest text-[18px] uppercase text-gray-900 mb-2">
                                {selectedItem.name}
                            </p>
                            <p className="text-gray-700 text-sm mb-4">
                                {selectedItem.description}
                            </p>

                            <div className="bg-yellow-50 border border-yellow-400 p-3 rounded">
                                <p className="text-yellow-800 text-sm">
                                    <strong>Cost:</strong> {selectedItem.currentPrice} framp card{selectedItem.currentPrice !== 1 ? 's' : ''}
                                </p>
                                <p className="text-yellow-800 text-sm">
                                    <strong>Remaining after:</strong> {completedStampCards - selectedItem.currentPrice} framp card{(completedStampCards - selectedItem.currentPrice) !== 1 ? 's' : ''}
                                </p>
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3 justify-end">
                            <button
                                type="button"
                                onClick={handleCancelPurchase}
                                disabled={purchasing}
                                className="bg-gray-300 hover:bg-gray-400 disabled:opacity-50 text-gray-800 px-6 py-2 font-['Jersey_10'] tracking-widest uppercase text-[17px] tracking-widest transition-colors"
                                style={{ boxShadow: '0 3px 0 0 rgba(0, 0, 0, 0.3)' }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleConfirmPurchase}
                                disabled={purchasing}
                                className="hover:opacity-90 disabled:opacity-50 text-white px-6 py-2 font-['Jersey_10'] tracking-widest uppercase text-[17px] tracking-widest transition-opacity"
                                style={{ boxShadow: '0 3px 0 0 rgba(0, 0, 0, 0.3)', backgroundColor: '#5A2929' }}
                            >
                                {purchasing ? 'Purchasing...' : 'Confirm Purchase'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* How do I earn framps? modal */}
            {showEarnModal && (
                <div className="fixed inset-0 z-[210] flex items-center justify-center">
                    <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setShowEarnModal(false)} />
                    <div
                        className="relative bg-offwhite border-2 border-gray-800 p-6 max-w-lg w-full mx-4 z-[211] flex flex-col"
                        style={{ boxShadow: '0 4px 0 0 rgba(0,0,0,0.5)', maxHeight: '80vh' }}
                    >
                        <h2 className="text-maroon uppercase font-['Jersey_10'] tracking-widest text-[20px] mb-4 shrink-0">
                            How do I earn framps?
                        </h2>
                        <hr className="border-gold mb-4 shrink-0" />

                        <div className="overflow-y-auto habbo-scroll flex-1 pr-1 text-sm text-gray-700 space-y-4">

                            <div>
                                <h3 className="font-bold text-maroon uppercase font-['Jersey_10'] tracking-widest text-[15px] mb-1">Community Activity</h3>
                                <ul className="space-y-1 list-disc list-inside">
                                    <li><b>Posting</b> on your profile</li>
                                    <li>Posting a <b>status</b> to the FranklySphere</li>
                                    <li><b>Liking</b> other members' posts.</li>
                                    <li>Giving <b>Luv</b> to other members.</li>
                                </ul>
                            </div>

                            <div>
                                <h3 className="font-bold text-maroon uppercase font-['Jersey_10'] tracking-widest text-[15px] mb-1">Skilling Tools</h3>
                                <ul className="space-y-1 list-disc list-inside">
                                    <li>Own a <b>Habbo Skill Fishing Rod</b> (purchasable from the Framp Shack) and fishing in-game.</li>
                                    <li>Own a <b>Habbo Skill Gardening Fork</b> (also from the Framp Shack) and garden in-game.</li>
                                </ul>
                            </div>

                            <div>
                                <h3 className="font-bold text-maroon uppercase font-['Jersey_10'] tracking-widest text-[15px] mb-1">Minigames</h3>
                                <ul className="space-y-1 list-disc list-inside">
                                    <li>Play <b>Lobby Burst</b> daily for a choice of either submitting a losing score for 1-3 framps, or a winning score for many more!</li>
                                </ul>
                            </div>

                            <div>
                                <h3 className="font-bold text-maroon uppercase font-['Jersey_10'] tracking-widest text-[15px] mb-1">Staff Awards</h3>
                                Staff members may award framps to players during <b>events, competitions, and giveaways</b>, so keep an eye on Frankly News and the Discord for upcoming events!
                            </div>

                            <div>
                                <h3 className="font-bold text-maroon uppercase font-['Jersey_10'] tracking-widest text-[15px] mb-1">What are framp cards?</h3>
                                <p>Every <b>10 framps</b> you collect automatically completes a framp card. Completed framp cards are the currency used to redeem items in the Framp Shack - the more you collect, the more you can spend!</p>
                            </div>

                        </div>

                        <div className="mt-4 shrink-0 flex justify-end">
                            <button
                                type="button"
                                onClick={() => setShowEarnModal(false)}
                                className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-6 py-2 font-['Jersey_10'] tracking-widest uppercase text-[17px] transition-colors"
                                style={{ boxShadow: '0 3px 0 0 rgba(0,0,0,0.3)' }}
                            >
                                Got it!
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default StampShack;